import {
  Autocomplete,
  TextField,
  Box,
  Button,
  Typography,
  ListSubheader,
  ListSubheaderProps,
} from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { FeatureAccessContext } from "#next/components/base/FeatureAccess";
import { trpc } from "#next/utils/trpc";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";

export interface IAvailableMediaCategory {
  group: string;
  label: string;
  textEmbedding: Array<number>;
}

function NestedListSubheader(props: ListSubheaderProps) {
  return (
    <ListSubheader sx={{ backgroundColor: "unset" }} disableSticky {...props} />
  );
}

NestedListSubheader.muiSkipListHighlight = true;
export default NestedListSubheader;

export const MediaSearch = ({
  closeQuickSearchModal,
}: {
  closeQuickSearchModal: () => void;
}) => {
  const router = useRouter();
  const [availableMediaCategories, setAvailableMediaCategories] = useState<
    IAvailableMediaCategory[]
  >([]);
  const [selectedMediaCategory, setSelectedMediaCategory] = useState<
    string | null
  >(null);
  const handleMediaCategoryChange = (
    event: any,
    value: string,
    reason: string,
  ) => {
    if (reason === "clear") {
      setSelectedMediaCategory(null);
    } else {
      setSelectedMediaCategory(value);
    }
  };

  const featureAccess = useContext(FeatureAccessContext);
  const { data: mediaOntology } = trpc.customSearch.getMediaOntology.useQuery(
    undefined,
    {
      refetchOnWindowFocus: false,
      enabled: featureAccess.featureMediaCategoryIsEnabled,
      staleTime: Infinity,
    },
  );

  useEffect(() => {
    if (!mediaOntology) return;
    const sortedCategories = mediaOntology.sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    setAvailableMediaCategories(sortedCategories);
  }, [mediaOntology]);

  if (!featureAccess.featureMediaCategoryIsEnabled) {
    return (
      <Typography variant="body1" sx={{ mt: 4 }}>
        Your organization does not have access to Media Search features. Contact{" "}
        <i>admin@pyrratech.com</i> to upgrade.
      </Typography>
    );
  }

  const handleClick = () => {
    const quickSearchId = uuidv4();
    const data = {
      searchTerms: null,
      searchType: "media",
      mediaCategory: selectedMediaCategory,
    };
    sessionStorage.setItem(quickSearchId, JSON.stringify(data));
    router.push({
      pathname: "/search",
      query: { searchId: quickSearchId, tr: "30d" },
    });
    closeQuickSearchModal();
  };

  return (
    <Box sx={{ mt: 4 }} justifyContent="center" display="flex">
      <Autocomplete //Should make this a separate component for reuse in Explorer filters
        id="media-category-labels"
        options={availableMediaCategories}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        onInputChange={handleMediaCategoryChange}
        size="small"
        sx={{ width: "100%" }}
        renderInput={(params) => (
          <TextField {...params} label="Media Category" />
        )}
      />
      <Button
        variant="outlined"
        size="small"
        sx={{ ml: 2 }}
        onClick={handleClick}
        disabled={selectedMediaCategory ? false : true}
      >
        Go
      </Button>
    </Box>
  );
};
